import {copyText} from '@github-ui/copy-to-clipboard'
import {KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Dialog, FormControl, IconButton, SegmentedControl, Select} from '@primer/react'
import {useEffect, useMemo, useState} from 'react'

import {filterTracesByThread, shouldHaveTracesAvailable} from '../../utils/trace-utils'
import styles from './TraceDisplay.module.css'
import {TraceList} from './TraceList'
import {useTraceManager} from './TraceManagerContext'
import {useTraceState} from './TraceProvider'
import {TraceTable} from './TraceTable'
import {TraceVisualizer} from './TraceVisualizer'
import {computeTreeLists, decorateTree} from './utilities/tree-utilities'

const GRAPH_TAB = 0
const TABLE_TAB = 1
const LIST_TAB = 2

export interface TraceDisplayProps {
  sharedThreadChannel: string | null | undefined
  threadId: string | null
}

export function TraceDisplay({threadId, sharedThreadChannel}: TraceDisplayProps) {
  const {traceInfo} = useTraceState()
  const traceManager = useTraceManager()
  const [selectedTab, setSelectedTab] = useState(0)
  const [verboseMode, setVerboseMode] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  // Only show traces that belong to the current thread
  const traces = useMemo(() => {
    const isSharedThread = !!sharedThreadChannel
    return filterTracesByThread(traceInfo?.traces, isSharedThread ? undefined : threadId)
  }, [traceInfo?.traces, threadId, sharedThreadChannel])

  const lastKey = traces ? Array.from(traces.keys()).at(-1) : null
  const lastTrace = lastKey ? traces?.get(lastKey) : null
  const [selectedTraceId, setSelectedTraceId] = useState<string>(lastKey ?? '')

  const handleDeleteTrace = async () => {
    if (threadId && selectedTraceId && traceInfo?.traces) {
      const traceData = traceInfo.traces.get(selectedTraceId)
      if (traceData?.actualTraceId) {
        // Close the dialog before the async delete so its focus trap can
        // restore focus while the DOM is still mounted. Without this,
        // REMOVE_TRACE causes the component to return null which tears down
        // the Dialog simultaneously — the focus trap then calls .focus() on a
        // detached element, which triggers pathological memory growth in Safari.
        setShowDeleteConfirmation(false)
        try {
          await traceManager.deleteTrace(threadId, traceData.actualTraceId)
          setSelectedTraceId('')
        } catch {
          // On failure the trace still exists — keep it selected so the user can retry.
        }
      }
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true)
  }

  // Update selectedTraceId when traces change
  useEffect(() => {
    if (traces && traces.size > 0) {
      const newLastKey = Array.from(traces.keys()).at(-1)
      if (newLastKey && (!selectedTraceId || !traces.has(selectedTraceId))) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedTraceId(newLastKey)
      }
    }
  }, [traces, selectedTraceId])

  // Close trace display when switching to a thread with no traces
  useEffect(() => {
    // Skip if already hidden to avoid redundant dispatches that create new state objects
    if (!traceInfo?.visible) return

    const isSharedThread = !!sharedThreadChannel
    const shouldHaveTraces = shouldHaveTracesAvailable(traceInfo?.traces, threadId, isSharedThread)

    if (!shouldHaveTraces) {
      void traceManager.showTraceDisplay(false, threadId)
    }
  }, [traceInfo?.visible, traceInfo?.traces, threadId, traceManager, sharedThreadChannel])

  const selectedTraceData = useMemo(() => {
    if (selectedTraceId && traces) {
      return traces.get(selectedTraceId) ?? null
    }
    return lastTrace ?? null
  }, [selectedTraceId, traces, lastTrace])

  const selectedSpanNode = selectedTraceData?.spanNode ?? null

  // Calculate the decorated tree data once and pass it down to all child components
  const decoratedTreeData = useMemo(() => {
    if (!selectedSpanNode) return null
    return decorateTree(selectedSpanNode)
  }, [selectedSpanNode])

  // Pre-compute the tree lists for TraceList component
  // saves significant time on verbose / filtered mode
  const treeLists = useMemo(() => {
    if (!decoratedTreeData) return null
    return computeTreeLists(decoratedTreeData)
  }, [decoratedTreeData])

  // null checks, useful to ensure no undefined variables
  if (!traces || traces.size === 0 || !selectedSpanNode || !decoratedTreeData || !treeLists) {
    return null
  }

  return (
    <div className={styles.traceDisplay}>
      <div className={styles.controls}>
        <FormControl>
          <FormControl.Label>Select trace</FormControl.Label>
          <Select value={selectedTraceId} onChange={e => setSelectedTraceId(e.target.value)}>
            {/* traces can come in any order, this ensures they are sorted by timestamp (ascending) */}
            {Array.from(traces.entries())
              .sort(([, traceDataA], [, traceDataB]) => traceDataA.timestamp.localeCompare(traceDataB.timestamp))
              .map(([messageId, traceData]) => (
                <Select.Option key={messageId} value={messageId}>
                  {traceData.timestamp}
                </Select.Option>
              ))}
          </Select>
        </FormControl>
        <div className={styles.rightControls}>
          <ActionMenu>
            <ActionMenu.Button icon={KebabHorizontalIcon} aria-label="More options" />
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.Item onSelect={() => void copyText(JSON.stringify(selectedSpanNode))}>
                  Copy Trace JSON
                </ActionList.Item>
                {selectedTraceData?.requestId && (
                  <ActionList.Item onSelect={() => void copyText(selectedTraceData.requestId!)}>
                    Copy Request ID
                  </ActionList.Item>
                )}
                {selectedTraceData?.threadId && (
                  <ActionList.Item onSelect={() => void copyText(selectedTraceData.threadId)}>
                    Copy Thread ID
                  </ActionList.Item>
                )}
                <ActionList.Divider />
                <ActionList.Item variant="danger" onSelect={handleDeleteClick}>
                  Delete Trace
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
          <IconButton
            icon={XIcon}
            aria-label="Close trace display"
            onClick={() => traceManager.showTraceDisplay(false, threadId)}
          />
        </div>
      </div>
      <ActionList.Divider />
      <div className={styles.controls}>
        <div>
          <SegmentedControl aria-label="Selected tab" onChange={setSelectedTab}>
            <SegmentedControl.Button selected={selectedTab === GRAPH_TAB}>Graph</SegmentedControl.Button>
            <SegmentedControl.Button selected={selectedTab === TABLE_TAB}>Table</SegmentedControl.Button>
            <SegmentedControl.Button selected={selectedTab === LIST_TAB}>List</SegmentedControl.Button>
          </SegmentedControl>
        </div>
        <div>
          <SegmentedControl aria-label="Verbose mode toggle" onChange={index => setVerboseMode(index === 1)}>
            <SegmentedControl.Button selected={!verboseMode}>Filtered</SegmentedControl.Button>
            <SegmentedControl.Button selected={verboseMode}>Verbose</SegmentedControl.Button>
          </SegmentedControl>
        </div>
      </div>
      {selectedTab === GRAPH_TAB && <TraceVisualizer root={decoratedTreeData} verboseMode={verboseMode} />}
      {selectedTab === TABLE_TAB && <TraceTable root={decoratedTreeData} verboseMode={verboseMode} />}
      {selectedTab === LIST_TAB && <TraceList treeLists={treeLists} verboseMode={verboseMode} />}
      {showDeleteConfirmation && (
        <Dialog
          footerButtons={[
            {buttonType: 'default', content: 'Cancel', onClick: () => setShowDeleteConfirmation(false)},
            {
              buttonType: 'danger',
              content: 'Delete Trace',
              onClick: () => void handleDeleteTrace(),
            },
          ]}
          onClose={() => setShowDeleteConfirmation(false)}
          title="Delete Trace"
        >
          Are you sure you want to delete this trace? This action cannot be undone and will permanently remove the trace
          data from secure storage.
        </Dialog>
      )}
    </div>
  )
}
